import React, { useEffect, useState, useCallback } from 'react';
import { Fingerprint, ScanFace, Lock, Loader2, CheckCircle2, MessageSquare, HandCoins, X, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const isWebAuthnSupported = typeof window !== 'undefined' && !!(window.PublicKeyCredential && navigator.credentials);

const b64urlToBuffer = (str) => {
  const padded = str + '==='.slice((str.length + 3) % 4);
  const bin = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

const bufferToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const PROVIDER_LABELS = { wave: 'Wave', orange: 'Orange Money', mtn: 'MTN MoMo', moov: 'Moov Money' };

export const WithdrawCard = ({ cagnotte, onWithdrawn }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [elig, setElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('guide'); // guide | otp | success
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState('');
  const [result, setResult] = useState(null);

  const isYako = cagnotte.type === 'Yako';
  // Joyeuse → couleur gaie ; Yako → couleur sombre
  const gradient = isYako
    ? 'linear-gradient(135deg, #4A148C 0%, #1f0a33 100%)'
    : 'linear-gradient(135deg, #d97757 0%, #f2b134 100%)';

  const load = useCallback(async () => {
    if (!user) { setElig(null); return; }
    try {
      const { data } = await api.get(`/cagnottes/${cagnotte.id}/can-withdraw`);
      setElig(data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[WithdrawCard] can-withdraw', e?.response?.status);
    }
  }, [cagnotte.id, user]);

  useEffect(() => { load(); }, [load, cagnotte.raised, cagnotte.status]);

  const finish = (data) => {
    setResult(data);
    setStep('success');
  };

  const closeAfterSuccess = () => {
    setOpen(false);
    if (result && onWithdrawn) onWithdrawn();
  };

  const withdrawBiometric = async () => {
    setBusy(true);
    try {
      const { data: opts } = await api.post('/webauthn/auth/options');
      const publicKey = {
        ...opts,
        challenge: b64urlToBuffer(opts.challenge),
        allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuffer(c.id) })),
      };
      const assertion = await navigator.credentials.get({ publicKey });
      if (!assertion) throw new Error('Vérification annulée');
      const credential = {
        id: assertion.id,
        rawId: bufferToB64url(assertion.rawId),
        type: assertion.type,
        response: {
          clientDataJSON: bufferToB64url(assertion.response.clientDataJSON),
          authenticatorData: bufferToB64url(assertion.response.authenticatorData),
          signature: bufferToB64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bufferToB64url(assertion.response.userHandle) : null,
        },
        clientExtensionResults: assertion.getClientExtensionResults?.() || {},
      };
      const { data } = await api.post(`/cagnottes/${cagnotte.id}/withdraw`, { method: 'webauthn', credential });
      finish(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Vérification impossible';
      toast({ title: 'Échec de la vérification', description: String(detail) });
    } finally { setBusy(false); }
  };

  const startOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/send-otp', { phone: user.phone });
      setStep('otp');
      if (data.devCode) toast({ title: 'Code envoyé (mode dev)', description: `Code : ${data.devCode}` });
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'envoyer le code SMS." });
    } finally { setBusy(false); }
  };

  const withdrawOtp = async () => {
    if (otp.trim().length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cagnottes/${cagnotte.id}/withdraw`, { method: 'otp', otp: otp.trim() });
      finish(data);
    } catch (err) {
      toast({ title: 'Code refusé', description: err?.response?.data?.detail || 'Vérification impossible.' });
    } finally { setBusy(false); }
  };

  // Already withdrawn → confirmation card for the owner
  if (elig?.isOwner && (elig.status === 'withdrawn' || cagnotte.status === 'withdrawn')) {
    return (
      <div className="rounded-3xl bg-white border border-black/5 p-6" data-testid="withdrawn-card">
        <div className="flex items-center gap-2 text-[#128C4A]">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-[14px] font-semibold">Cagnotte retirée</span>
        </div>
        <p className="mt-1.5 text-[12.5px] text-neutral-600">Les fonds ont été transférés vers votre compte bénéficiaire. Consultez vos notifications pour le détail.</p>
      </div>
    );
  }

  if (!elig?.isOwner) return null;

  return (
    <>
      <div className="rounded-3xl bg-white border border-black/5 p-6" data-testid="withdraw-card">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-semibold text-neutral-500">
          <Lock className="w-3.5 h-3.5" /> Espace ayant droit
        </div>
        <button
          type="button"
          data-testid="withdraw-cagnotte-btn"
          disabled={!elig.canWithdraw}
          onClick={() => { setStep('guide'); setOtp(''); setResult(null); setOpen(true); }}
          className="btn-anim mt-3 w-full h-12 rounded-full text-white text-[14px] font-semibold flex items-center justify-center gap-2 shadow-lg disabled:opacity-45 disabled:cursor-not-allowed transition-transform hover:scale-[1.01]"
          style={{ background: gradient }}
        >
          <HandCoins className="w-4.5 h-4.5" /> Retirer la cagnotte
          <Lock className="w-3.5 h-3.5 opacity-80" />
        </button>
        {!elig.canWithdraw && elig.reason && (
          <p className="mt-2.5 text-[12px] text-neutral-500 leading-snug" data-testid="withdraw-reason">{elig.reason}</p>
        )}
        {elig.canWithdraw && (
          <p className="mt-2.5 text-[12px] text-neutral-500">
            {formatN(elig.amount)} FCFA disponibles · vérification biométrique requise
          </p>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="withdraw-modal">
          <div className="w-full max-w-[440px] rounded-3xl bg-white overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 text-white flex items-center justify-between" style={{ background: gradient }}>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4.5 h-4.5" />
                <span className="text-[14.5px] font-semibold">Retrait sécurisé</span>
              </div>
              <button type="button" onClick={closeAfterSuccess} aria-label="Fermer" data-testid="withdraw-modal-close" className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {step === 'guide' && (
              <div className="p-6">
                <div className="text-center">
                  <div className="text-[22px] font-semibold text-[#1a1d1a]">{formatN(elig.amount)} FCFA</div>
                  <div className="text-[12.5px] text-neutral-500">
                    vers votre compte {PROVIDER_LABELS[elig.beneficiary?.provider] || 'Mobile Money'}
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-[#f5f4ef] p-4 text-center">
                    <Fingerprint className="w-7 h-7 mx-auto text-[#5a8268]" />
                    <div className="mt-2 text-[12px] font-semibold text-[#1a1d1a]">Étape 1</div>
                    <div className="text-[11.5px] text-neutral-600 leading-snug">Empreinte, Face ID ou code de l'appareil</div>
                  </div>
                  <div className="rounded-2xl bg-[#f5f4ef] p-4 text-center">
                    <ScanFace className="w-7 h-7 mx-auto text-[#d97757]" />
                    <div className="mt-2 text-[12px] font-semibold text-[#1a1d1a]">Étape 2</div>
                    <div className="text-[11.5px] text-neutral-600 leading-snug">Validation instantanée et virement</div>
                  </div>
                </div>
                <Button
                  onClick={withdrawBiometric}
                  disabled={busy || !elig.hasPasskey || !isWebAuthnSupported}
                  data-testid="withdraw-biometric-btn"
                  className="btn-anim mt-5 w-full h-12 rounded-full text-white text-[13.5px] font-medium"
                  style={{ background: gradient }}
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                  Démarrer la vérification biométrique
                </Button>
                {!elig.hasPasskey && (
                  <p className="mt-2 text-[11.5px] text-amber-700 bg-amber-50 rounded-lg p-2.5 text-center">
                    Aucune passkey enregistrée — activez la biométrie depuis votre tableau de bord, ou utilisez le code SMS.
                  </p>
                )}
                <button
                  type="button"
                  onClick={startOtp}
                  disabled={busy}
                  data-testid="withdraw-otp-fallback-btn"
                  className="mt-3 w-full text-[12.5px] text-neutral-600 hover:text-[#1a1d1a] font-medium flex items-center justify-center gap-1.5 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Utiliser un code SMS à la place
                </button>
              </div>
            )}

            {step === 'otp' && (
              <div className="p-6 text-center">
                <MessageSquare className="w-8 h-8 mx-auto text-[#5a8268]" />
                <h3 className="mt-3 text-[16px] font-semibold text-[#1a1d1a]">Code de vérification</h3>
                <p className="mt-1 text-[12.5px] text-neutral-600">Saisissez le code à 6 chiffres envoyé au {user?.phone}.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  data-testid="withdraw-otp-input"
                  className="mt-4 w-full h-12 rounded-2xl bg-[#f5f4ef] text-center text-[20px] tracking-[0.4em] font-semibold outline-none focus:ring-2 focus:ring-[#d97757]/40"
                  placeholder="••••••"
                />
                <Button
                  onClick={withdrawOtp}
                  disabled={busy || otp.length < 4}
                  data-testid="withdraw-otp-confirm-btn"
                  className="btn-anim mt-4 w-full h-12 rounded-full text-white text-[13.5px] font-medium"
                  style={{ background: gradient }}
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Confirmer le retrait
                </Button>
              </div>
            )}

            {step === 'success' && result && (
              <div className="p-6 text-center" data-testid="withdraw-success">
                <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center bg-[#128C4A]/10 text-[#128C4A]">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="mt-4 text-[20px] font-semibold text-[#1a1d1a]">Retrait validé !</h3>
                <p className="mt-2 text-[13.5px] text-neutral-600">
                  <span className="font-semibold text-[#1a1d1a]">{formatN(result.amount)} FCFA</span> en cours de virement vers votre compte {PROVIDER_LABELS[result.provider] || 'Mobile Money'}.
                </p>
                <p className="mt-1 text-[11.5px] text-neutral-500">Identité validée par {result.verification}.</p>
                <p className="mt-3 text-[11px] text-emerald-700 bg-emerald-50 rounded-lg p-2">Virement réel via CinetPay — l'argent est envoyé sur le Mobile Money du bénéficiaire.</p>
                <Button onClick={closeAfterSuccess} data-testid="withdraw-success-close" className="btn-anim mt-4 w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white">Fermer</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
